import {
  TARE_PROFILE_VALIDATION_FAILED,
  UNIT_PROFILE_VALIDATION_FAILED,
  type HoldingUnitCapabilities,
  type HoldingUnitProfile,
  type TareProfileMode,
  type TareProfileValidationIssue,
  type TareProfileValidationResult,
  type UnitProfileValidationIssue,
  type UnitProfileValidationResult,
} from '@crv/contracts';

import { TareProfileDomainError, UnitProfileDomainError } from './errors.js';
import {
  UNIT_PROFILE_CAPABILITY_NAMES,
  findUnitProfileTemplateForTuple,
  getUnitProfileTemplateDefinition,
} from './templates.js';

export interface UnitProfileValidationInput extends HoldingUnitProfile {
  templateKey: string;
  profileCategory: string;
  fixedIdPolicy: string;
  capabilityPolicy?: Partial<HoldingUnitCapabilities> | null;
}

export interface TareProfileValidationInput {
  mode: string;
  defaultTareLb?: number | null;
}

export type UnitProfileStructuralField =
  | 'code'
  | 'templateKey'
  | 'unitType'
  | 'identityMode'
  | 'containerKind'
  | 'productKind'
  | 'fixedIdPolicy';

export const UNIT_PROFILE_STRUCTURAL_FIELDS = [
  'code',
  'templateKey',
  'unitType',
  'identityMode',
  'containerKind',
  'productKind',
  'fixedIdPolicy',
] as const satisfies readonly UnitProfileStructuralField[];

type UnitProfileStructuralComparable = Record<UnitProfileStructuralField, string>;

export function validateUnitProfileTuple(
  profile: HoldingUnitProfile,
): UnitProfileValidationResult {
  const issues: UnitProfileValidationIssue[] = [];
  if (!findUnitProfileTemplateForTuple(profile)) {
    issues.push({
      code: 'invalid_tuple_family',
      field: 'unitType',
      message: 'Unit profile tuple is not covered by a legal unit profile template.',
    });
  }

  return toUnitProfileValidationResult(issues);
}

export function validateUnitProfile(
  profile: UnitProfileValidationInput,
): UnitProfileValidationResult {
  const issues: UnitProfileValidationIssue[] = [];
  const template = getUnitProfileTemplateDefinition(profile.templateKey);

  if (!template) {
    issues.push({
      code: 'invalid_template_key',
      field: 'templateKey',
      message: 'Unit profile templateKey is not recognized.',
    });
    return toUnitProfileValidationResult(issues);
  }

  const tupleResult = validateUnitProfileTuple(profile);
  issues.push(...tupleResult.issues);

  addMismatchIssue(issues, 'profileCategory', profile.profileCategory, template.profileCategory);
  addMismatchIssue(issues, 'unitType', profile.unitType, template.unitType);
  addMismatchIssue(issues, 'identityMode', profile.identityMode, template.identityMode);
  addMismatchIssue(issues, 'containerKind', profile.containerKind, template.containerKind);
  addMismatchIssue(issues, 'productKind', profile.productKind, template.productKind);
  addMismatchIssue(issues, 'fixedIdPolicy', profile.fixedIdPolicy, template.fixedIdPolicy);

  if (profile.fixedIdPolicy === 'fixed_asset_required' && profile.identityMode !== 'fixed_asset_cycle') {
    issues.push({
      code: 'fixed_id_policy_mismatch',
      field: 'fixedIdPolicy',
      message: 'Fixed-ID policy requires a fixed_asset_cycle identity mode.',
    });
  }

  if (profile.identityMode === 'fixed_asset_cycle' && profile.fixedIdPolicy !== 'fixed_asset_required') {
    issues.push({
      code: 'fixed_id_policy_mismatch',
      field: 'fixedIdPolicy',
      message: 'fixed_asset_cycle profiles must use fixed_asset_required policy.',
    });
  }

  if (profile.capabilityPolicy) {
    issues.push(
      ...validateCapabilityPolicyWithinTemplate(
        template.templateKey,
        profile.capabilityPolicy,
      ).issues,
    );
  }

  return toUnitProfileValidationResult(issues);
}

export function assertUnitProfileValid(profile: UnitProfileValidationInput): void {
  const result = validateUnitProfile(profile);
  if (result.isValid) {
    return;
  }

  throw new UnitProfileDomainError(
    UNIT_PROFILE_VALIDATION_FAILED,
    'Unit profile failed validation.',
    result,
  );
}

export function validateCapabilityPolicyWithinTemplate(
  templateKey: string,
  capabilityPolicy: Partial<HoldingUnitCapabilities>,
): UnitProfileValidationResult {
  const issues: UnitProfileValidationIssue[] = [];
  const template = getUnitProfileTemplateDefinition(templateKey);

  if (!template) {
    issues.push({
      code: 'invalid_template_key',
      field: 'templateKey',
      message: 'Unit profile templateKey is not recognized.',
    });
    return toUnitProfileValidationResult(issues);
  }

  for (const capabilityName of UNIT_PROFILE_CAPABILITY_NAMES) {
    if (capabilityPolicy[capabilityName] === true && !template.maxCapabilities[capabilityName]) {
      issues.push({
        code: 'capability_exceeds_template',
        field: capabilityName,
        message: `${capabilityName} is not allowed by the ${template.templateKey} template.`,
      });
    }
  }

  return toUnitProfileValidationResult(issues);
}

export function deriveUnitProfileCapabilityPolicy(
  templateKey: string,
  capabilityPolicy?: Partial<HoldingUnitCapabilities> | null,
): HoldingUnitCapabilities {
  const template = getUnitProfileTemplateDefinition(templateKey);
  if (!template) {
    throw new UnitProfileDomainError(
      UNIT_PROFILE_VALIDATION_FAILED,
      'Unit profile templateKey is not recognized.',
      { templateKey },
    );
  }

  const merged: HoldingUnitCapabilities = {
    ...template.defaultCapabilityPolicy,
    ...(capabilityPolicy ?? {}),
  };
  const result = validateCapabilityPolicyWithinTemplate(templateKey, merged);
  if (!result.isValid) {
    throw new UnitProfileDomainError(
      UNIT_PROFILE_VALIDATION_FAILED,
      'Unit profile capability policy exceeds the template ceiling.',
      result,
    );
  }

  return merged;
}

export function validateTareProfile(
  profile: TareProfileValidationInput,
): TareProfileValidationResult {
  const issues: TareProfileValidationIssue[] = [];

  switch (profile.mode) {
    case 'fixed_default':
      if (profile.defaultTareLb == null) {
        issues.push({
          code: 'fixed_default_tare_required',
          field: 'defaultTareLb',
          message: 'fixed_default tare profiles require defaultTareLb.',
        });
      }
      break;
    case 'fixed_asset_master':
    case 'manual_required':
    case 'none':
      if (profile.defaultTareLb != null) {
        issues.push({
          code: 'default_tare_not_allowed',
          field: 'defaultTareLb',
          message: `${profile.mode} tare profiles cannot store defaultTareLb.`,
        });
      }
      break;
    default:
      issues.push({
        code: 'invalid_tare_profile_mode',
        field: 'mode',
        message: 'Tare profile mode is not recognized.',
      });
      break;
  }

  return {
    isValid: issues.length === 0,
    issues,
  };
}

export function assertTareProfileValid(profile: TareProfileValidationInput): void {
  const result = validateTareProfile(profile);
  if (result.isValid) {
    return;
  }

  throw new TareProfileDomainError(
    TARE_PROFILE_VALIDATION_FAILED,
    'Tare profile failed validation.',
    result,
  );
}

export function validateUnitProfileTareCompatibility(
  profile: Pick<UnitProfileValidationInput, 'templateKey' | 'fixedIdPolicy'>,
  tareProfile: { mode: string } | null | undefined,
): UnitProfileValidationResult {
  const issues: UnitProfileValidationIssue[] = [];
  const template = getUnitProfileTemplateDefinition(profile.templateKey);

  if (!template) {
    issues.push({
      code: 'invalid_template_key',
      field: 'templateKey',
      message: 'Unit profile templateKey is not recognized.',
    });
    return toUnitProfileValidationResult(issues);
  }

  if (!tareProfile) {
    return toUnitProfileValidationResult(issues);
  }

  if (!template.allowedTareModes.includes(tareProfile.mode as TareProfileMode)) {
    issues.push({
      code: 'tare_profile_incompatible',
      field: 'tareProfileId',
      message: `${tareProfile.mode} tare profiles are not allowed by the ${template.templateKey} template.`,
    });
  }

  if (
    tareProfile.mode === 'fixed_asset_master'
    && profile.fixedIdPolicy !== 'fixed_asset_required'
  ) {
    issues.push({
      code: 'tare_profile_incompatible',
      field: 'tareProfileId',
      message: 'fixed_asset_master tare profiles require a fixed-ID unit profile.',
    });
  }

  return toUnitProfileValidationResult(issues);
}

export function validateUnitProfileStructuralEdit(
  existing: UnitProfileStructuralComparable,
  candidate: UnitProfileStructuralComparable,
): UnitProfileValidationResult {
  const issues: UnitProfileValidationIssue[] = [];

  for (const field of UNIT_PROFILE_STRUCTURAL_FIELDS) {
    if (existing[field] !== candidate[field]) {
      issues.push({
        code: 'structural_edit_not_allowed',
        field,
        message: `${field} is structural and requires clone + deactivate.`,
      });
    }
  }

  return toUnitProfileValidationResult(issues);
}

function addMismatchIssue(
  issues: UnitProfileValidationIssue[],
  field:
    | 'profileCategory'
    | 'unitType'
    | 'identityMode'
    | 'containerKind'
    | 'productKind'
    | 'fixedIdPolicy',
  actual: string,
  expected: string,
): void {
  if (actual === expected) {
    return;
  }

  issues.push({
    code:
      field === 'fixedIdPolicy'
        ? 'fixed_id_policy_mismatch'
        : field === 'profileCategory'
          ? 'profile_category_mismatch'
          : 'template_tuple_mismatch',
    field,
    message: `${field} must be ${expected} for the selected unit profile template.`,
  });
}

function toUnitProfileValidationResult(
  issues: UnitProfileValidationIssue[],
): UnitProfileValidationResult {
  return {
    isValid: issues.length === 0,
    issues,
  };
}
